import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowUpRight, Check } from "lucide-react";
import { useSeo } from "../hooks/useSeo";
import { Reveal, StaggerGroup, staggerItem } from "../components/Reveal";
import { Marquee } from "../components/Marquee";
import { CTASection } from "../components/CTASection";
import { IMAGES } from "../config/site";
import {
  PRACTICE_AREAS, VALUE_CARDS, CREDIBILITY, AUTHORITY, REVIEWS_PREVIEW,
} from "../data/content";

const HEADLINE_LINES = ["The Law Office of", "Ronald K. Nolen, LLC"];
const easeOut = [0.22, 1, 0.36, 1];

const SCHEMA = {
  "@context": "https://schema.org",
  "@type": "LegalService",
  name: "The Law Office of Ronald K. Nolen, LLC",
  description:
    "Chicago-based legal counsel serving businesses, entrepreneurs, athletes, creators, property owners, and individuals in transactional and high-stakes legal matters.",
  url: "https://www.rknolenlaw.org/",
  telephone: "+1-312-394-0184",
  email: "rknolenesq@rknolenlaw.com",
  areaServed: "Chicago, Illinois",
  address: {
    "@type": "PostalAddress",
    streetAddress: "9723 S Western Ave",
    addressLocality: "Chicago",
    addressRegion: "IL",
    postalCode: "60643",
    addressCountry: "US",
  },
  openingHoursSpecification: {
    "@type": "OpeningHoursSpecification",
    dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
    opens: "09:00",
    closes: "16:00",
  },
  founder: { "@type": "Person", name: "Ronald K. Nolen, Esq.", jobTitle: "Founder & Managing Attorney" },
};

export default function Home() {
  useSeo({
    title: "The Law Office of Ronald K. Nolen, LLC | Chicago Legal Counsel",
    description:
      "Chicago-based legal counsel serving businesses, entrepreneurs, athletes, creators, property owners, and individuals in transactional and high-stakes legal matters.",
    path: "/",
    schema: SCHEMA,
  });

  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1.05, 1.15]);

  return (
    <>
      {/* ============================ HERO ============================ */}
      <section ref={heroRef} className="relative h-[100svh] min-h-[640px] w-full overflow-hidden" data-testid="home-hero">
        <motion.div style={{ y: imgY, scale: imgScale }} className="absolute inset-0">
          <img
            src={IMAGES.heroRiver}
            alt="The Chicago River winding between downtown high-rises on a bright day"
            className="h-full w-full object-cover"
          />
        </motion.div>
        {/* Restrained navy gradient for text readability only */}
        <div className="absolute inset-0 bg-gradient-to-r from-navy/70 via-navy/30 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-t from-navy/45 via-transparent to-transparent" />

        <div className="relative mx-auto flex h-full max-w-7xl flex-col justify-center px-5 md:px-8">
          <motion.p
            initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="mb-5 text-xs font-600 uppercase tracking-label text-gold-soft"
          >
            Chicago-Based Legal Counsel
          </motion.p>

          <h1 className="max-w-5xl font-serif text-[2.4rem] font-500 leading-[1.06] text-white sm:text-6xl lg:text-[5.25rem]">
            {HEADLINE_LINES.map((line, i) => (
              <span key={i} className="block overflow-hidden pb-[0.12em]">
                <motion.span
                  className="block"
                  initial={{ y: "110%" }}
                  animate={{ y: "0%" }}
                  transition={{ duration: 0.9, delay: 0.35 + i * 0.14, ease: easeOut }}
                >
                  {line}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.9 }}
            className="mt-7 max-w-xl text-base leading-relaxed text-white/85 md:text-lg"
          >
            Strategic legal representation for professionals, entrepreneurs, athletes, creators,
            property owners, and individuals who need experienced counsel and decisive advocacy.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.05 }}
            className="mt-9 flex flex-wrap items-center gap-4"
          >
            <Link to="/contact" data-testid="hero-cta-consultation"
              className="group inline-flex items-center gap-2 rounded-sm bg-gold px-8 py-4 text-sm font-600 text-navy transition-colors duration-300 hover:bg-gold-soft">
              Request a Consultation
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" strokeWidth={1.8} />
            </Link>
            <Link to="/services" data-testid="hero-cta-services"
              className="inline-flex items-center gap-2 rounded-sm border border-white/40 px-8 py-4 text-sm font-600 text-white transition-colors duration-300 hover:border-gold hover:text-gold-soft">
              Explore Our Services
            </Link>
            <Link to="/team" data-testid="hero-link-team"
              className="group inline-flex items-center gap-1.5 px-2 py-4 text-sm font-500 text-white/85 transition-colors hover:text-white">
              Meet Our Team
              <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" strokeWidth={1.8} />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* ======================= CREDIBILITY BAR ======================= */}
      <section className="border-b border-navy/10 bg-navy" data-testid="credibility-bar">
        <div className="mx-auto grid max-w-7xl grid-cols-2 gap-px md:grid-cols-4">
          {CREDIBILITY.map((c) => (
            <div key={c} className="flex items-center gap-3 px-5 py-6 md:justify-center md:py-7">
              <span className="h-1.5 w-1.5 rotate-45 bg-gold" />
              <span className="text-sm font-500 text-white/85">{c}</span>
            </div>
          ))}
        </div>
      </section>

      {/* ========================= INTRODUCTION ========================= */}
      <section className="bg-warmwhite py-20 md:py-32" data-testid="home-intro">
        <div className="mx-auto grid max-w-7xl gap-12 px-5 md:px-8 lg:grid-cols-12">
          <Reveal className="lg:col-span-5">
            <p className="text-xs font-600 uppercase tracking-label text-gold">The Firm</p>
            <h2 className="mt-5 font-serif text-4xl font-500 leading-[1.05] text-navy sm:text-5xl">
              Strategic Counsel. Personal Attention. Serious Advocacy.
            </h2>
          </Reveal>
          <Reveal delay={0.15} className="lg:col-span-6 lg:col-start-7">
            <p className="text-lg leading-relaxed text-bluegray-text">
              The Law Office of Ronald K. Nolen, LLC provides reliable, ethical legal representation
              rooted in preparation, responsiveness, and a clear understanding of what is at stake.
              From transactions and negotiations to litigation and dispute resolution, the firm helps
              clients protect their interests and move forward with confidence.
            </p>
            <Link to="/team/ronald-k-nolen" data-testid="intro-meet-ronald"
              className="group mt-8 inline-flex items-center gap-2 border-b-2 border-gold pb-1 text-sm font-600 text-navy transition-colors hover:text-gold">
              Meet Ronald K. Nolen
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" strokeWidth={1.8} />
            </Link>
          </Reveal>
        </div>
      </section>

      <Marquee words={["Strategic", "Competent", "Ethical", "Reliable"]} />

      {/* ======================= PRACTICE AREAS ======================= */}
      <section className="bg-warmwhite py-20 md:py-28" data-testid="home-practice-areas">
        <div className="mx-auto max-w-7xl px-5 md:px-8">
          <Reveal className="max-w-3xl">
            <p className="text-xs font-600 uppercase tracking-label text-gold">Practice Areas</p>
            <h2 className="mt-4 font-serif text-4xl font-500 leading-[1.05] text-navy sm:text-5xl">
              Legal Services Built Around What Matters Most
            </h2>
          </Reveal>
          <StaggerGroup className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {PRACTICE_AREAS.map((p) => {
              const Icon = p.icon;
              return (
                <motion.div key={p.anchor} variants={staggerItem}>
                  <Link
                    to={`/services#${p.anchor}`}
                    data-testid={`practice-card-${p.anchor}`}
                    className="group flex h-full flex-col rounded-sm border border-navy/10 bg-white p-8 transition-colors duration-300 hover:border-gold"
                  >
                    <Icon className="h-8 w-8 text-navy transition-colors group-hover:text-gold" strokeWidth={1.2} />
                    <h3 className="mt-6 font-serif text-2xl font-500 text-navy">{p.title}</h3>
                    <p className="mt-3 flex-1 text-sm leading-relaxed text-bluegray-text">{p.blurb}</p>
                    <span className="mt-6 inline-flex items-center gap-1.5 text-sm font-600 text-navy transition-colors group-hover:text-gold">
                      Learn More
                      <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" strokeWidth={1.8} />
                    </span>
                  </Link>
                </motion.div>
              );
            })}
          </StaggerGroup>
        </div>
      </section>

      {/* ======================= WHY CHOOSE ======================= */}
      <section className="bg-cream py-20 md:py-28" data-testid="home-why-choose">
        <div className="mx-auto max-w-7xl px-5 md:px-8">
          <Reveal className="max-w-3xl">
            <p className="text-xs font-600 uppercase tracking-label text-gold">Why Clients Choose the Firm</p>
            <h2 className="mt-4 font-serif text-4xl font-500 leading-[1.05] text-navy sm:text-5xl">
              Counsel That Sees the Full Picture
            </h2>
          </Reveal>
          <StaggerGroup className="mt-14 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {VALUE_CARDS.map((v) => (
              <motion.div key={v.n} variants={staggerItem}
                className="rounded-sm border border-navy/10 bg-white p-8">
                <span className="font-serif text-3xl font-500 text-gold">{v.n}</span>
                <h3 className="mt-3 font-serif text-2xl font-500 text-navy">{v.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-bluegray-text">{v.body}</p>
              </motion.div>
            ))}
          </StaggerGroup>
        </div>
      </section>

      {/* ======================= AUTHORITY ======================= */}
      <section className="bg-navy py-20 md:py-28" data-testid="home-authority">
        <div className="mx-auto max-w-7xl px-5 md:px-8">
          <Reveal className="max-w-3xl">
            <p className="text-xs font-600 uppercase tracking-label text-gold-soft">Authority</p>
            <h2 className="mt-4 font-serif text-4xl font-500 leading-[1.05] text-white sm:text-5xl">
              Experience That Extends Beyond the Courtroom
            </h2>
          </Reveal>
          <StaggerGroup className="mt-14 grid gap-px border border-white/10 bg-white/10 sm:grid-cols-2 lg:grid-cols-3">
            {AUTHORITY.map((a) => (
              <motion.div key={a} variants={staggerItem} className="flex items-start gap-4 bg-navy p-8">
                <Check className="mt-1 h-5 w-5 flex-shrink-0 text-gold" strokeWidth={1.5} />
                <p className="text-base font-500 leading-snug text-white/90">{a}</p>
              </motion.div>
            ))}
          </StaggerGroup>
        </div>
      </section>

      {/* ======================= FOUNDER FEATURE ======================= */}
      <section className="bg-warmwhite py-20 md:py-28" data-testid="home-founder">
        <div className="mx-auto grid max-w-7xl items-center gap-12 px-5 md:px-8 lg:grid-cols-2 lg:gap-20">
          <Reveal className="order-2 lg:order-1">
            <p className="text-xs font-600 uppercase tracking-label text-gold">Founder & Managing Attorney</p>
            <h2 className="mt-4 font-serif text-4xl font-500 leading-[1.05] text-navy sm:text-5xl">
              Meet Ronald K. Nolen
            </h2>
            <p className="mt-6 text-lg leading-relaxed text-bluegray-text">
              Ronald K. Nolen is a Chicago-born attorney, educator, negotiator, and legal advocate whose
              work spans real estate, contract law, and sports and entertainment law. He brings clients
              a combination of courtroom experience, clear and competent counsel, and deep Chicago roots.
            </p>
            <Link to="/team/ronald-k-nolen" data-testid="founder-read-bio"
              className="group mt-8 inline-flex items-center gap-2 rounded-sm bg-navy px-8 py-4 text-sm font-600 text-white transition-colors hover:bg-navy-light">
              Read Ronald's Biography
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" strokeWidth={1.8} />
            </Link>
          </Reveal>
          <Reveal delay={0.15} className="order-1 lg:order-2">
            <div className="relative mx-auto max-w-md">
              <div className="absolute -inset-4 rounded-sm bg-gradient-to-br from-cream to-bluegray/40" />
              <div className="absolute inset-0 -z-10 rounded-full bg-gold/10 blur-3xl" />
              <div className="relative overflow-hidden rounded-sm rounded-tr-[3rem] border border-navy/10 bg-white">
                <img
                  src={IMAGES.ronald}
                  alt="Ronald K. Nolen, Founder and Managing Attorney"
                  className="h-[30rem] w-full object-cover object-top"
                />
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ======================= REVIEWS PREVIEW ======================= */}
      {/* PLACEHOLDER REVIEWS — REPLACE BEFORE PUBLIC LAUNCH */}
      <section className="bg-cream py-20 md:py-28" data-testid="home-reviews-preview">
        <div className="mx-auto max-w-7xl px-5 md:px-8">
          <div className="flex flex-wrap items-end justify-between gap-6">
            <Reveal>
              <p className="text-xs font-600 uppercase tracking-label text-gold">Testimonials</p>
              <h2 className="mt-4 font-serif text-4xl font-500 leading-[1.05] text-navy sm:text-5xl">What Clients Say</h2>
            </Reveal>
            <Reveal delay={0.1}>
              <Link to="/reviews" data-testid="reviews-preview-view-all"
                className="group inline-flex items-center gap-2 border-b-2 border-gold pb-1 text-sm font-600 text-navy transition-colors hover:text-gold">
                View Client Reviews
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" strokeWidth={1.8} />
              </Link>
            </Reveal>
          </div>
          <StaggerGroup className="mt-14 grid gap-6 md:grid-cols-3">
            {REVIEWS_PREVIEW.map((r, i) => (
              <motion.figure key={i} variants={staggerItem}
                className="flex h-full flex-col rounded-sm border border-navy/10 bg-white p-8"
                data-testid={`review-preview-${i}`}>
                <span className="font-serif text-5xl leading-none text-gold">&ldquo;</span>
                <h3 className="mt-2 font-serif text-xl font-500 leading-snug text-navy">{r.title}</h3>
                <blockquote className="mt-3 flex-1 text-base leading-relaxed text-navy/80 line-clamp-5">
                  {r.quote}
                </blockquote>
                <figcaption className="mt-6 flex items-center justify-between border-t border-navy/10 pt-4 text-sm">
                  <span className="font-600 text-navy">— {r.name}</span>
                  <span className="text-bluegray-text">{r.date}</span>
                </figcaption>
              </motion.figure>
            ))}
          </StaggerGroup>
        </div>
      </section>

      {/* ======================= FINAL CTA ======================= */}
      <CTASection testid="home-final-cta" />
    </>
  );
}
